import React from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import classnames from 'classnames';
import { Input, Label, FormGroup } from 'reactstrap';
import { Controller } from 'react-hook-form';

// Adapter: the field components in this file consume redux-form's {input, meta}
// contract. This lets react-hook-form forms reuse them unchanged via Controller,
// so consumers can migrate off redux-form one at a time.
//   <RHFField name="x" control={control} component={SelectField} ...rest />
// `onValueChange` (optional) fires only on user-driven changes — like redux-form's
// Field `onChange` interceptor — and NOT on programmatic setValue()/reset(), so
// side-effects (confirmation modals, dependent fetches) don't fire on reinitialize.
export const RHFField = ({ name, control, component: Component, rules, onValueChange, ...rest }) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field, fieldState, formState }) => (
      <Component
        input={{
          name: field.name,
          value: field.value,
          onChange: (e) => {
            field.onChange(e);
            if (onValueChange) onValueChange(e && e.target ? e.target.value : e);
          },
          onBlur: field.onBlur,
          // only checkbox/switch fields use `checked`; leave others alone
          checked: typeof field.value === 'boolean' ? field.value : undefined,
        }}
        meta={{
          touched: fieldState.isTouched,
          // RHF marks a field touched on BLUR, so a row the user never focused —
          // one added programmatically — has an error nothing renders. Fields need
          // the submit signal too.
          submitted: formState.isSubmitted,
          error: fieldState.error ? fieldState.error.message : undefined,
        }}
        {...rest}
      />
    )}
  />
);

// Adapt redux-form validators (return undefined | error message) to react-hook-form's
// validate (return true | error message). Pass getValues for cross-field validators.
export const rfRules = (validators, getValues) => ({
  validate: (value) => {
    for (const v of validators) {
      const err = v(value, getValues ? getValues() : {});
      if (err) return err;
    }
    return true;
  },
});

// 0.6em rendered at ~8px, which is how a validation message can be on screen and still be
// missed — see PDECOM-5970 AC4.
export const ErrorMessage = ({ error }) => {
  return (
    <div style={{ fontSize: '0.8rem' }} className="mt-1 text-danger">
      {error}
    </div>
  );
};

export const renderInputField = ({ input, disabled, type, className, meta: { touched, submitted, error }, ...rest }) => {
  // Blur alone is not enough: a row the form generated (a new grouping's staging
  // hostname) is never focused, so on submit it blocks the save while showing
  // nothing — which is the PDECOM-5970 AC4 symptom on the path this adds.
  const show = (touched || submitted) && !!error;
  return (
    <div>
      <input
        {...input}
        type={type}
        disabled={disabled}
        {...rest}
        // className is merged, not replaced: it used to be assigned after the
        // spread, so a caller's own classes were silently dropped. The old
        // expression also stringified `false` into the class list when untouched.
        className={['form-control', className, show ? 'border border-danger' : ''].filter(Boolean).join(' ')}
      />
      {show && <ErrorMessage error={error} />}
    </div>
  );
};

export const SelectField = ({ input, options, isMulti, style, disabled, creatable, ...rest }) => {
  // react-select expects null/undefined for unset values, not the empty string Redux Form
  // hands out by default. Normalise both options and the current value so:
  //   - chips render labels for pre-loaded records (input.value items missing label/value)
  //   - the dropdown isn't held in a strange state by an empty-string value
  const normalize = (v) => {
    if (v == null || v === '') return null;
    return v.label != null ? v : { ...v, label: v.name, value: v.id };
  };

  const normalizedOptions = options ? options.map(normalize).filter(Boolean) : options;

  const value = isMulti
    ? (Array.isArray(input.value) ? input.value.map(normalize).filter(Boolean) : null)
    : normalize(input.value);

  // `creatable` lets users type a value not in the option list — needed for Business Central
  // sources, where the source-column list isn't available to populate the dropdown.
  const SelectComponent = creatable ? CreatableSelect : Select;

  return (
    <SelectComponent
      isDisabled={disabled}
      {...input}
      {...rest}
      value={value}
      style={style}
      options={normalizedOptions}
      isMulti={isMulti}
      formatCreateLabel={creatable ? inputValue => `Add "${inputValue}"` : undefined}
      onBlur={() => {}}
    />
  );
};

export function CheckboxGroup({ input, options, meta: { touched, error }, disabled }) {
  return (
    <React.Fragment>
      <div className=" d-flex flex-row">
        {options.map((option, idx) => {
          return (
            <FormGroup check inline key={idx} className="test">
              <Input
                key={idx}
                name={`${input.name}[${idx}]`}
                className={idx === 0 ? '' : 'ms-3'}
                id={option.label}
                label={option.label}
                type="checkbox"
                checked={input.value && input.value.indexOf(option.name) !== -1}
                disabled={disabled}
                onChange={event => {
                  const newValue = [...input.value];
                  if (event.target.checked) {
                    newValue.push(option.name);
                  } else {
                    newValue.splice(newValue.indexOf(option.name), 1);
                  }
                  input.onBlur(newValue);
                  return input.onChange(newValue);
                }}
              />
              <Label check>
                {option.label}
              </Label>
              {' '}
            </FormGroup>
          );
        })}
      </div>
      {touched && error && <ErrorMessage error={error} />}
    </React.Fragment>
  );
}

export function Switch({ input, ...rest }) {
  return <FormGroup switch><Input {...input} {...rest} type="switch" role="switch" onChange={e => input.onChange(e.target.checked)} /></FormGroup>;
}

export function InputField({ input, className, meta: { touched, error }, ...rest }) {
  return (
    <div className={classnames(className, 'd-flex flex-column')}>
      <Input {...input} {...rest} className={classnames({ 'border border-danger': touched && error })} />
      {touched && error && <ErrorMessage error={error} />}
    </div>
  );
}

export function NativeSelect({ input, options, meta: { touched, error }, ...rest }) {
  return (
    <div className='d-flex flex-column'>
      <Input type="select" {...input} {...rest}>
        <option disabled />
        {options &&
          options.map(option => (
            <option key={option.value} value={option.value}>
              {option.name}
            </option>
          ))}
      </Input>
      {touched && error && <ErrorMessage error={error} />}
    </div>
  );
}

export function RadioButtons({ input, options, meta: { touched, error }, ...rest }) {
  return (
    <FormGroup check>
      {options &&
        options.map(option => {
          return (
          <Label check className="btn btn-outline" key={option.value}>
            <Input type="radio" {...input} {...rest} value={option.value} 
            onChange={() => { rest.handler.handleSyncVersionClick(option.value);} }
            className={classnames({ 'border border-danger': touched && error })} 
            checked={ rest.selected == option.value}
            />{' '}
            {option.name}
          </Label>
      );})}
      {touched && error && <ErrorMessage error={error} />}
    </FormGroup>
  );
}